import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import type { TransactionHash } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";

/**
 * Prepares a withdraw transaction
 */
export const prepareWithdrawTransaction = async (
    unwindingTimestampMs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    const unwindingTimestamp = Math.floor(unwindingTimestampMs / 1000);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "withdraw",
            args: [unwindingTimestamp],
        }),
    };
};

/**
 * Executes a withdraw transaction
 */
export const gatewayWithdrawTransaction = async (
    unwindingTimestamp: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareWithdrawTransaction(unwindingTimestamp, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a withdraw transaction
 */
export const estimateWithdrawTransactionGas = async (
    unwindingTimestamp: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareWithdrawTransaction(unwindingTimestamp, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
